import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertBanner = ({ className = "" }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const alertTypes = {
    critical: {
      icon: 'AlertTriangle',
      bgColor: 'bg-error/10',
      borderColor: 'border-error',
      textColor: 'text-error',
      iconColor: 'text-error'
    },
    warning: {
      icon: 'AlertCircle',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning',
      textColor: 'text-warning',
      iconColor: 'text-warning'
    },
    info: {
      icon: 'Info',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent',
      textColor: 'text-accent',
      iconColor: 'text-accent'
    }
  };

  const sampleAlerts = [
    {
      id: 1,
      type: 'critical',
      title: 'Multiple Failed Access Attempts',
      message: 'Device SL-003 (Conference Room) has recorded 5 failed attempts in the last 2 minutes',
      timestamp: new Date(),
      deviceId: 'SL-003',
      action: 'Investigate',
      actionPath: '/alerts-notifications'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Low Battery Alert',
      message: 'Device SL-001 (Main Entrance) battery level is at 15%',
      timestamp: new Date(Date.now() - 300000),
      deviceId: 'SL-001',
      action: 'Schedule Maintenance',
      actionPath: '/settings'
    },
    {
      id: 3,
      type: 'critical',
      title: 'Unauthorized Access Pattern',
      message: 'Anomalous access detected: Entry at 2:30 AM outside normal business hours',
      timestamp: new Date(Date.now() - 600000),
      deviceId: 'SL-002',
      action: 'Review Logs',
      actionPath: '/access-logs'
    },
    {
      id: 4,
      type: 'info',
      title: 'Device Back Online',
      message: 'Device SL-004 (Storage Room) has reconnected to the network',
      timestamp: new Date(Date.now() - 900000),
      deviceId: 'SL-004',
      action: 'View Status',
      actionPath: '/dashboard'
    }
  ];

  useEffect(() => {
    // Simulate real-time alerts
    const alertInterval = setInterval(() => {
      const randomAlert = sampleAlerts?.[Math.floor(Math.random() * sampleAlerts?.length)];
      const newAlert = {
        ...randomAlert,
        id: Date.now(),
        timestamp: new Date()
      };
      
      setAlerts(prev => [newAlert, ...prev?.slice(0, 2)]);
      setCurrentAlert(newAlert);
      setIsVisible(true);

      // Auto-hide after 10 seconds for non-critical alerts
      if (newAlert?.type !== 'critical') {
        setTimeout(() => {
          setIsVisible(false);
        }, 10000);
      }
    }, 25000);

    return () => clearInterval(alertInterval);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentAlert(null);
    }, 300);
  };

  const handleAction = (actionPath) => {
    navigate(actionPath);
    handleDismiss();
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (!currentAlert || !isVisible) {
    return null;
  }

  const typeConfig = alertTypes?.[currentAlert?.type];

  return (
    <div className={`mb-6 security-transition ${className}`}>
      <div className={`
        ${typeConfig?.bgColor} ${typeConfig?.borderColor} 
        border-l-4 rounded-lg security-shadow-card
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
      `}>
        <div className="flex items-start space-x-4 p-4">
          {/* Icon */}
          <div className={`flex-shrink-0 ${currentAlert?.type === 'critical' ? 'security-pulse' : ''}`}>
            <Icon 
              name={typeConfig?.icon} 
              size={24} 
              className={typeConfig?.iconColor}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className={`text-base font-semibold ${typeConfig?.textColor} mb-1`}>
                  {currentAlert?.title}
                </h3>
                <p className="text-sm text-foreground mb-2">
                  {currentAlert?.message}
                </p>
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span>Device: {currentAlert?.deviceId}</span>
                  <span>{formatTimeAgo(currentAlert?.timestamp)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                {currentAlert?.action && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAction(currentAlert?.actionPath)}
                    className={`${typeConfig?.textColor} border-current hover:bg-current/10`}
                  >
                    {currentAlert?.action}
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  iconName="X"
                  className="text-muted-foreground hover:text-foreground"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar for auto-dismiss */}
        {currentAlert?.type !== 'critical' && (
          <div className="h-1 bg-border/20 rounded-b-lg overflow-hidden">
            <div 
              className={`h-full ${typeConfig?.borderColor?.replace('border-', 'bg-')} animate-pulse`}
              style={{
                animation: 'progress-bar 10s linear forwards'
              }}
            />
          </div>
        )}
      </div>
      <style jsx>{`
        @keyframes progress-bar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default AlertBanner;